import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import { modals } from '@/mixins/modal';
import { guid } from '@/mixins/guid';
import dropsLoader from '@/mixins/components/drops-loader';

export default Vue.component('confirm-change', {
  template: require('./template.html'),
  name: 'confirm-change',
  components: {
    dropsLoader,
  },
  data() {
    return {
      loading: false,
      confirmErrors: [],
      requirements: [],
      downgrade: false,
      selectedfirst: 'Choose...',
      modals: {
        confirmChange: false,
        selectPlan: false,
      },
    };
  },
  mixins: [modals, guid],
  props: ['selected-plan', 'can-update-hosting', 'hosting'],
  watch: {
    'modals.confirmChange': function o(o) {
      if (o) {
        this.resetModal();
        this.checkCanChange();
      }
    },
    'modals.selectPlan': function o(o) {
      if (o) {
        this.resetModal();
      }
    },
  },
  mounted() {
    // used for modal mixin
    this.cls = '.adminUi-modalOverlay';
  },
  computed: {
    ...mapGetters([
      'currentSite',
      'account',
      'currentUser',
      'priceSource',
    ]),
    canChangePlan() {
      return (this.selectedPlan &&
        this.currentSite.tier !== this.selectedPlan &&
        this.currentUser.owner) || (this._isBundle && this.canUpdateHosting);
    },
    failures() {
      return _.filter(this.requirements, r => !r.pass);
    },
    tiers() {
      return this.priceSource.tiers;
    },
  },
  methods: {
    resetModal() {
      this.confirmErrors = [];
      this.requirements = [];
      this.selectedfirst = 'Choose...';
      this.downgrade = false;
    },
    async setDiscountHosting() {
      if (this._isBundle && this.currentUser.paymentMethod) {
        this.loading = true;
        const notification = {
          id: this.guid(),
          message: 'Plan successfully updated!',
          type: 'success',
        };
        try {
          const newSiteVals = await this._SEND([{
            name: 'setDiscountHosting',
            payload: {
              siteId: this.currentSite._id,
              hosting: this.hosting,
            },
          }]);
          if (!this.currentSite.discount) {
            this.$store.dispatch('setGlobalValue', {
              item: this.currentSite,
              key: 'discount',
              value: {
                hosting: this.hosting,
              },
            });
          } else {
            this.$store.dispatch('setGlobalValue', {
              item: this.currentSite.discount,
              key: 'hosting',
              value: this.hosting,
            });
          }
          _.forOwn(newSiteVals, (value, key) => {
            this.$store.dispatch('setGlobalValue', {
              item: this.currentSite,
              key,
              value,
            });
          });
          this.$emit('set-initial-hosting', this.hosting);
          this.$log.debug('Successfully added changed plan: ', newSiteVals);
          this.toggleModal('confirmChange');
        } catch (e) {
          this.$log.error(e.message || e);
          notification.type = 'error';
          notification.message = e.message || e;
          await this._reportError({
            error: e,
            fn: 'setDiscountHosting',
          });
        } finally {
          this.loading = false;
          this.$store.dispatch('addNotification', notification);
          this._trackEvent({
            type: 'set_discount_site',
            meta: {
              site_name: this.currentSite.alias,
              site_type: this.currentSite.type,
            },
          });
        }
      }
    },
    getDiscount() {
      let price = this.tiers[this.selectedPlan].price;
      const discountList = this.priceSource.discounts;
      const type = this._isBundle ? 'bundle' : null;
      if (discountList && discountList[type]) {
        const discount = (parseInt(discountList[type], 10) / 100) * price;
        price -= discount;
      }
      return price;
    },
    onNewPlanChange() {
      this.requirements = [];
      this.runDowngradeChecker(this.tiers[this.selectedfirst].limits);
    },
    async selectNewPlan() {
      this.loading = true;
      const notification = {
        id: this.guid(),
        type: 'success',
        message: 'Successfully published website!',
      };
      try {
        const newSiteVals = await this.$lightning([{
          name: 'goLiveSite',
          payload: {
            siteId: this.currentSite._id,
            selectedPlan: this.selectedfirst,
          },
        }]);
        _.forOwn(newSiteVals, (value, key) => {
          this.$store.dispatch('setGlobalValue', {
            item: this.currentSite,
            key,
            value,
          });
        });
        this.toggleModal('selectPlan');
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        notification.type = 'error';
        notification.message = message;
      } finally {
        this.loading = false;
        this.$store.dispatch('addNotification', notification);
      }
    },
    async changeSitePlan() {
      if (this.selectedPlan === 'Free' || this.currentUser.paymentMethod || this._isExempt) {
        this.loading = true;
        const notification = {
          id: this.guid(),
          message: 'Plan successfully updated!',
          type: 'success',
        };
        const oldPlan = this.currentSite.tier;
        try {
          const newSiteVals = await this._SEND([{
            name: 'changeSitePlan',
            payload: {
              siteId: this.currentSite._id,
              selectedPlan: this.selectedPlan,
            },
          }]);
          _.forOwn(newSiteVals, (value, key) => {
            this.$store.dispatch('setGlobalValue', {
              item: this.currentSite,
              key,
              value,
            });
          });
          this.$log.debug('Successfully added changed plan: ', newSiteVals);
          this.toggleModal('confirmChange');
        } catch (e) {
          this.$log.error(e.message || e);
          notification.type = 'error';
          notification.message = e.message || e;
          await this._reportError({
            error: e,
            fn: 'changeSitePlan',
          });
        } finally {
          this.loading = false;
          this.$store.dispatch('addNotification', notification);
          this._trackEvent({
            type: 'change_site_plan',
            meta: {
              site_name: this.currentSite.alias,
              site_type: this.currentSite.type,
              old_plan: oldPlan,
              new_plan: this.selectedPlan,
            },
          });
        }
      }
    },
    checkCanChange() {
      this.loading = true;
      try {
        // these should only check when downgrading plans
        const isPremium = this.currentSite.tier.match(/premium/i);
        const isStarter = this.currentSite.tier.match(/starter/i);
        const wantsStarter = this.selectedPlan.match(/starter/i);
        const wantsFree = this.selectedPlan.match(/free/i);
        // we need to make sure current site meets requirements for downgrade
        if ((isPremium && wantsStarter) || (isStarter && wantsFree) || (isPremium && wantsFree)) {
          this.downgrade = this.selectedPlan;
          this.runDowngradeChecker(this.tiers[this.selectedPlan].limits);
        }
      } catch (e) {
        this.$log.error(e.message || e);
      } finally {
        this.loading = false;
      }
    },
    runDowngradeChecker(params) {
      _.forOwn(params, (value, key) => {
        const tail = value > 0 ? ' or less' : '';
        let items;
        if (key === 'recycled') {
          items = 'recycled droplets';
        } else if (key === 'dynamics') {
          items = 'dynamic data items';
        } else {
          items = _.lowerCase(key);
        }
        const item = {
          message: `Site has ${value} ${items}${tail}.`,
          pass: true,
        };
        if (key === 'forms') {
          const hasForms = this.currentSite[key];
          if (hasForms && hasForms.length > value) {
            item.pass = false;
          }
          // allow legacy users to squeek by
          item.currentVal = hasForms ? hasForms.length : 0;
        } else if (key === 'collaborators') {
          const { collaborators = {} } = this.currentSite;
          const list = Object.keys(collaborators);
          if (list.length > value) {
            item.pass = false;
          }
          item.currentVal = list.length;
        } else if (key === 'subUsers') {
          item.pass = true;
          item.currentVal = 0;
        } else if (key === 'dynamics') {
          const dynamics = this._allDynamicItems(this.currentSite);
          item.currentVal = dynamics.length;
          if (dynamics.length > value) {
            item.pass = false;
          }
        } else {
          const siteItem = this.currentSite[key];
          if (siteItem && siteItem.length > value) {
            item.pass = false;
          }
          item.currentVal = siteItem.length;
        }
        if (key !== 'subUsers') {
          this.requirements.push(item);
        }
      });
    },
    getPassValue(pass) {
      return pass ? 'adminUi-iconCheck' : 'adminUi-iconTimes';
    },
    getPrice() {
      let price = 0;
      const plan = this.currentSite.lifecycle === 'design' ? this.selectedfirst : this.selectedPlan;
      if (plan && !this._isBundle && !this._isExempt) {
        price = this.tiers[plan].price;
      }
      return price;
    },
    goToBilling() {
      this.toggleModal('confirmChange');
      setTimeout(() => {
        this.$router.push({ path: '/billing' });
      }, 300);
    },
    toggleModal(type) {
      if (type === 'confirmChange') {
        this.cls = '#confirm-plan-change';
      } else {
        this.cls = '#select-hosting-plan';
      }
      this.modals[type] = !this.modals[type];
      const content = this.$refs[type];
      if (this.modals[type]) {
        this.modelMoveOut(content);
      } else {
        this.modalMoveIn(content);
      }
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/websites/edit-site/options/confirm-change/index.js